import React from 'react';
import { Link } from 'react-router-dom';

const CleanMindPrivacyPolicy: React.FC = () => {
  return (
    <section className="section">
      <div className="container">
        <div style={{ marginBottom: '2rem' }}>
          <Link 
            to="/" 
            style={{ 
              color: 'var(--accent-color)', 
              textDecoration: 'none',
              fontSize: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            ← Back to Home
          </Link>
        </div>
        
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{ 
            fontSize: '2.5rem', 
            fontWeight: '700',
            marginBottom: '1rem',
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Ai Storage Cleaner - CleanMind Privacy Policy
          </h1>
          <div className="section-divider"></div>
        </div>

        <div style={{ 
          maxWidth: '800px', 
          margin: '0 auto',
          lineHeight: '1.6',
          fontSize: '1rem'
        }}>
          <p style={{ marginBottom: '2rem', fontSize: '1rem' }}>
            <strong>Effective Date:</strong> September 20, 2025<br/>
            <strong>Last Updated:</strong> September 20, 2025
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>1. Introduction</h2>
          
          <p style={{ marginBottom: '1rem' }}>
            Welcome to Ai Storage Cleaner - CleanMind ("we," "our," or "us"), a privacy-focused AI-powered storage management mobile application. We are committed to protecting your privacy and ensuring the security of your personal information through data minimization, transparency, and user control.
          </p>
          
          <p style={{ marginBottom: '1rem' }}>
            This Privacy Policy explains how we collect, use, store, and protect your information when you use our mobile application available on iOS platform. By using CleanMind, you acknowledge that you have read, understood, and agree to this Privacy Policy.
          </p>

          <p style={{ marginBottom: '1rem' }}><strong>Key Privacy Principles:</strong></p>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li><strong>Privacy by Design</strong>: Privacy protection is built into our app from the ground up</li>
            <li><strong>Data Minimization</strong>: We only collect data necessary for app functionality</li>
            <li><strong>Local Processing</strong>: All photo analysis happens on your device, not our servers</li>
            <li><strong>No Data Sales</strong>: We never sell your personal information to third parties</li>
            <li><strong>User Control</strong>: You control your data and can delete it at any time</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>2. Information We Collect</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.1 Information You Provide Directly</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Photos and Videos</strong>: Media files you allow the app to analyze from your device gallery</li>
            <li><strong>Analysis Results</strong>: Duplicate detection results, similar image groupings, and storage recommendations (stored locally on your device)</li>
            <li><strong>App Settings</strong>: Your preferences for analysis sensitivity, file types to scan, and app configurations</li>
            <li><strong>Feedback</strong>: Support emails or feedback you send to support@rapidnova.com</li>
          </ul>

          <p style={{ marginBottom: '1rem' }}><strong>Important</strong>: CleanMind does NOT require account creation. All your data is stored locally on your device.</p>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.2 Information Automatically Collected</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Usage Analytics</strong>: Basic app usage patterns to improve functionality (via Firebase Analytics)</li>
            <li style={{ paddingLeft: '1rem' }}>Feature usage frequency (e.g., duplicate detection vs. similar image analysis)</li>
            <li style={{ paddingLeft: '1rem' }}>App performance metrics and crash reports</li>
            <li style={{ paddingLeft: '1rem' }}>General usage statistics (sessions, retention)</li>
            <li><strong>Device Information</strong>: Device model, operating system version, app version</li>
            <li><strong>Technical Data</strong>: Error logs and diagnostic information for app improvement</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>2.3 Information We Do NOT Collect</h3>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li><strong>Personal Identification</strong>: No names, email addresses, phone numbers, or personal identifiers</li>
            <li><strong>Location Data</strong>: No GPS coordinates or location tracking</li>
            <li><strong>Account Data</strong>: No user accounts, passwords, or authentication data</li>
            <li><strong>Photo Content</strong>: Your photos and analysis results are processed locally and not transmitted to our servers</li>
            <li><strong>Browsing History</strong>: No web browsing or external app usage tracking</li>
            <li><strong>Contacts</strong>: No access to your contact list or communication data</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>3. How We Use Your Information</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>3.1 Core App Functions (Local Processing)</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>AI-Powered Analysis</strong>: Analyze your photos and videos to identify duplicates, similar images, and large files using on-device machine learning</li>
            <li><strong>Local Storage</strong>: Save your analysis results and app preferences on your device only</li>
            <li><strong>Smart Recommendations</strong>: Provide personalized storage cleanup suggestions based on your photo library</li>
            <li><strong>Organization</strong>: Help categorize and organize your media files for easier management</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>3.2 App Improvement (Anonymous Analytics)</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Usage Analytics</strong>: Understand which features are most used to prioritize improvements</li>
            <li><strong>Performance Monitoring</strong>: Identify and fix crashes, bugs, and performance issues</li>
            <li><strong>Feature Development</strong>: Analyze usage patterns to develop new features users want</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>3.3 User Support</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Customer Support</strong>: Respond to support emails and feedback sent to support@rapidnova.com</li>
            <li><strong>Bug Reports</strong>: Help diagnose and resolve issues you report</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>3.4 Legal and Security</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Legal Compliance</strong>: Comply with applicable laws and platform requirements (App Store)</li>
            <li><strong>Security</strong>: Protect the integrity and security of our app and services</li>
          </ul>

          <p style={{ marginBottom: '2rem' }}><strong>Data Processing Location</strong>: All AI analysis and data storage happens locally on your device. Only anonymous analytics data is transmitted to our servers.</p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>4. Data Processing and Storage</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>4.1 AI Processing</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>100% Local Processing</strong>: All photo analysis and duplicate detection is performed entirely on your device using Apple's Core ML</li>
            <li><strong>No Cloud Processing</strong>: Your photos and analysis results are never sent to our servers or any cloud service</li>
            <li><strong>On-Device AI</strong>: We use device-based machine learning models for image analysis</li>
            <li><strong>Offline Functionality</strong>: The app works completely offline after installation</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>4.2 Data Storage</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Local Storage Only</strong>: All analysis results, preferences, and cached data are stored locally on your device</li>
            <li><strong>No Cloud Backup</strong>: We do not provide cloud sync or backup services</li>
            <li><strong>User Control</strong>: You can clear all data anytime through the app settings</li>
            <li><strong>App Deletion</strong>: All data is permanently deleted when you uninstall the app</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>4.3 Data Retention</h3>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li><strong>User-Controlled</strong>: Data is retained until you delete it or uninstall the app</li>
            <li><strong>No Server Storage</strong>: We don't store your personal data on any servers</li>
            <li><strong>Analytics Data</strong>: Anonymous usage analytics are retained for up to 14 months to improve the app</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>5. Data Sharing and Third Parties</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>5.1 We Do Not Sell Your Data</h3>
          <p style={{ marginBottom: '1rem' }}>We never sell, rent, or trade your personal information to third parties. This is a core principle of our privacy policy.</p>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>5.2 Third-Party Services We Use</h3>

          <h4 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'inherit' }}>Firebase Analytics (Google)</h4>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Purpose</strong>: Anonymous app usage analytics and crash reporting</li>
            <li><strong>Data Shared</strong>: Device type, app version, feature usage patterns, crash logs</li>
            <li><strong>Data NOT Shared</strong>: Your photos, analysis results, personal information</li>
            <li><strong>Privacy Policy</strong>: <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)' }}>Google Firebase Privacy Policy</a></li>
          </ul>

          <h4 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'inherit' }}>Apple Core ML (On-Device)</h4>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Purpose</strong>: AI-powered photo analysis and duplicate detection</li>
            <li><strong>Data Processing</strong>: Entirely on your device, no data leaves your device</li>
            <li><strong>Privacy Policy</strong>: <a href="https://www.apple.com/privacy/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-color)' }}>Apple Privacy Policy</a></li>
          </ul>

          <h4 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'inherit' }}>App Store Services</h4>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li><strong>Apple App Store</strong>: App distribution, in-app purchases (if applicable), app reviews</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>6. Data Security</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>6.1 Security Measures</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Local Storage Security</strong>: Your data is protected by your device's built-in security (iOS Keychain)</li>
            <li><strong>No Network Transmission</strong>: Your photos and analysis results never leave your device, eliminating transmission risks</li>
            <li><strong>App Sandboxing</strong>: The app operates in a secure sandbox environment provided by iOS</li>
            <li><strong>Regular Updates</strong>: We provide regular app updates to address security vulnerabilities</li>
            <li><strong>Minimal Permissions</strong>: We only request permissions essential for app functionality</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>6.2 Device Security Recommendations</h3>
          <p style={{ marginBottom: '0.5rem' }}>To protect your data, we recommend:</p>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li><strong>Device Lock</strong>: Use device passcode, Touch ID, or Face ID</li>
            <li><strong>App Updates</strong>: Keep the app updated to the latest version</li>
            <li><strong>OS Updates</strong>: Keep your device operating system updated</li>
            <li><strong>Device Security</strong>: Use reputable security software if available for your platform</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>7. Your Rights and Choices</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>7.1 Data Control (Since Data is Local)</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Full Access</strong>: You have complete access to all your data through the app interface</li>
            <li><strong>Instant Deletion</strong>: Clear all analysis results or app data through app settings</li>
            <li><strong>No Server Requests Needed</strong>: Since data is local, you don't need to request access or deletion</li>
            <li><strong>Export</strong>: Use the app's share features to export your analysis results anytime</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>7.2 App Permissions</h3>
          <p style={{ marginBottom: '0.5rem' }}>You can manage app permissions through your device settings:</p>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Photos</strong>: Required for accessing and analyzing your photo library</li>
            <li><strong>Storage</strong>: Required for saving analysis results locally on your device</li>
          </ul>
          <p style={{ marginBottom: '2rem' }}><strong>Note</strong>: You can revoke these permissions anytime, but it will limit app functionality.</p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>8. Children's Privacy</h2>
          <p style={{ marginBottom: '2rem' }}>Our app is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us, and we will delete such information.</p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>9. International Data Transfers</h2>
          <p style={{ marginBottom: '2rem' }}>If you are located outside the country where our servers are located, your information may be transferred to, stored, and processed in a different country. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards.</p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>10. Changes to This Privacy Policy</h2>
          <p style={{ marginBottom: '0.5rem' }}>We may update this Privacy Policy from time to time. We will notify you of any material changes by:</p>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li>Posting the updated policy in the app</li>
            <li>Sending you a notification through the app</li>
            <li>Posting a notice on our website</li>
          </ul>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>11. Contact Information</h2>
          <p style={{ marginBottom: '1rem' }}>If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:</p>
          
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>Email</h3>
          <p style={{ marginBottom: '1.5rem' }}>info@rapidnova.co</p>
          
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>Address</h3>
          <p style={{ marginBottom: '2rem' }}>
            60 Paya Lebar Road, #07-54<br/>
            Paya Lebar Square<br/>
            Singapore 409051
          </p>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem', color: 'inherit' }}>12. Mobile App Specific Information</h2>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>12.1 Platform Integration</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>iOS</strong>: Complies with Apple's App Store Review Guidelines and iOS privacy requirements</li>
            <li><strong>App Store Privacy Labels</strong>: Accurate privacy labels are provided on the App Store</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>12.2 Permissions Explained</h3>
          <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
            <li><strong>Photo Library</strong>: Only used to analyze photos for duplicates and storage optimization</li>
            <li><strong>Storage</strong>: Only for saving analysis results locally on your device</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', color: 'inherit' }}>12.3 No Background Activity</h3>
          <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem' }}>
            <li><strong>No Background Data Collection</strong>: The app only processes data when you're actively using it</li>
            <li><strong>No Background Location</strong>: No location tracking or background location access</li>
            <li><strong>No Background Network</strong>: No data transmission when app is not in use</li>
          </ul>

          <hr style={{ margin: '3rem 0', border: 'none', borderTop: '1px solid #eee' }} />

          <p style={{ marginBottom: '1rem', fontStyle: 'italic' }}>
            <strong>Acknowledgment</strong>: By downloading, installing, or using Ai Storage Cleaner - CleanMind, you acknowledge that you have read, understood, and agree to this Privacy Policy. If you do not agree with any part of this policy, please do not use our app.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CleanMindPrivacyPolicy;